import "./ProfileEditModal.scss";
import Modal from "../../../../shared/components/UI/Modal/Modal";
import {
  displayCloseModalDialog,
  displayNotSaved,
  displaySuccessUpdate,
  displayError,
} from "../../../../shared/services/Notifications/NotificationServices";
import useUserProfile, {
  RegionType,
  DataType,
  CamouflagesType,
} from "./useUserProfile";
import React, {
  useState,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import ProfileImages from "./components/ProfileImages";
import Sidebar from "./components/Sidebar";
import ProfileForm from "./components/ProfileForm";
import axios from "axios";

interface Camo {
  name: string;
  image: string;
}

interface CamoOption {
  id: string;
  name: string;
  image: string;
}

export type TabType =
  | "basic"
  | "identifier"
  | "showcase"
  | "account"
  | "privacy"
  | "settings";

export default function ProfileEditModal({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const username = localStorage.getItem("username") || "";
  const {
    fetchStatistics,
    fetchRegions,
    fetchUserData,
    patchUserData,
    uploadUserAvatar,
    uploadUserTheme,
    fetchCamouflages,
    fetchSpecialisations,
    fetchArmoryList,
  } = useUserProfile(username);

  const [camoOptions, setCamoOptions] = useState<CamoOption[]>([]);

  useEffect(() => {
    axios.get<Camo[]>("/info/camouflages/").then((res) => {
      setCamoOptions(
        res.data.map((c, i) => ({
          id: String(i),
          name: c.name,
          image: c.image,
        }))
      );
    });
  }, []);

  const [activeTab, setActiveTab] = useState<TabType>("basic");
  const [avatarUrl, setAvatarUrl] = useState("./modal/avatar.svg");
  const [themeUrl, setThemeUrl] = useState("./modal/theme.png");
  const [teams, setTeams] = useState<{ name: string; slug: string }[]>();
  const [openPassField, setOpenPassField] = useState<
    "password" | "newPassword" | "confirmPassword" | null
  >(null);
  const [fullArmoryList, setFullArmoryList] = useState<
    {
      id: string;
      firm_name: string;
    }[]
  >([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [regions, setRegions] = useState<RegionType[]>([]);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const themeInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    formState: { isDirty, dirtyFields, errors },
    setValue,
    getValues,
    reset,
    watch,
  } = useForm({ mode: "onChange" });

  useEffect(() => {
    register("camouflagesList");
  }, [register]);

  const onSubmit: SubmitHandler<any> = async (formData) => {
    const selectedArmory = fullArmoryList.find(
      (armory) => armory.firm_name === formData.selectedArmory
    );
    const changed: Partial<DataType> = {};
    Object.keys(dirtyFields).forEach(
      (k) => ((changed as any)[k] = formData[k])
    );
    changed.weapon_id = selectedArmory?.id;
    changed.camouflages = camoOptions.filter(
      (c: any, id: any) => formData.camouflagesList[id]
    );
    if (!Object.keys(changed).length) return;
    try {
      await patchUserData(changed, teams);
      displaySuccessUpdate();
      reset(formData, { keepDirty: false });
      setHasUnsavedChanges(false);
    } catch {
      displayError();
    }
  };

  useEffect(() => {
    const load = async () => {
      const userRes = await fetchUserData();
      console.log("1");
      const statsRes = await fetchStatistics();
      const regRes = await fetchRegions();
      const camouflagesRes = await fetchCamouflages();
      const specialisationsRes = await fetchSpecialisations();
      const armoryListRes = await fetchArmoryList(username);
      setFullArmoryList(armoryListRes.data);
      const armoryNames = armoryListRes.data.map((a: any) => a.firm_name);
      const profileTeam = userRes.data.user_teams?.find(
        (t: { slug: string; name: string; is_profile_team: boolean }) =>
          t.is_profile_team
      );
      const user_armory = armoryListRes.data.find(
        (t: { firm_name: string; id: string; is_profile_weapon: boolean }) =>
          t.is_profile_weapon === true
      )?.firm_name;
      const init = {
        ...userRes.data,
        experience: statsRes.data.experience,
        camouflagesList: camouflagesRes.data,
        team_slug: profileTeam?.name || "",
        armoryList: armoryNames,
        specList: specialisationsRes.data.map((c: { name: string }) => c.name),
        user_armory,
      };
      reset(init, { keepDirty: false });
      setTeams(userRes.data.user_teams);
      setAvatarUrl(init.user_avatar_link || "/modal/avatar.svg");
      setThemeUrl(userRes.data.user_banner_link || "/modal/theme.png");
      setRegions(regRes);
      setHasUnsavedChanges(false);
    };
    load();
  }, [reset]);

  useEffect(() => setHasUnsavedChanges(isDirty), [isDirty]);

  const togglePasswordVisibility = (field: typeof openPassField) =>
    setOpenPassField((prev) => (prev === field ? null : field));

  const blockIfUnsaved = () => {
    const values = getValues();
    const hasInput = Object.values(values).some((v) => v && v !== "");
    return hasUnsavedChanges && hasInput;
  };

  const closeModal = () => {
    if (blockIfUnsaved()) {
      displayCloseModalDialog(() => setIsOpen(false));
      return;
    }
    setIsOpen(false);
  };

  const changeActiveTab = (s: TabType) => {
    if (blockIfUnsaved()) {
      displayNotSaved();
      return;
    }
    setActiveTab(s);
  };

  const trigger = (r: React.RefObject<HTMLInputElement>) => r.current?.click();

  const handleFile =
    (type: "avatar" | "theme") =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }

      try {
        let processedFile = file;

        if (file.type === "image/png") {
          const imageBitmap = await createImageBitmap(file);
          const canvas = document.createElement("canvas");
          canvas.width = imageBitmap.width;
          canvas.height = imageBitmap.height;

          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Canvas context not available");

          ctx.drawImage(imageBitmap, 0, 0);
          const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92)
          );

          if (!blob) throw new Error("Failed to convert PNG to JPEG");

          processedFile = new File(
            [blob],
            file.name.replace(/\.png$/, ".jpg"),
            {
              type: "image/jpeg",
            }
          );
        }

        if (type === "avatar") await uploadUserAvatar(processedFile);
        else await uploadUserTheme(processedFile);

        const reader = new FileReader();
        reader.onload = (ev) => {
          if (!ev.target?.result) return;
          type === "avatar"
            ? setAvatarUrl(ev.target.result as string)
            : setThemeUrl(ev.target.result as string);
        };
        reader.readAsDataURL(processedFile);

        displaySuccessUpdate();
      } catch {
        displayError();
      }
    };

  return (
    <Modal isOpen={isOpen} closeModal={closeModal}>
      <div className="cabinet_profileeditmodal">
        <div className="cabinet_profileeditmodal-left">
          <ProfileImages
            themeUrl={themeUrl}
            avatarUrl={avatarUrl}
            triggerThemeInput={() => trigger(themeInputRef)}
            triggerAvatarInput={() => trigger(avatarInputRef)}
            themeInputRef={themeInputRef}
            avatarInputRef={avatarInputRef}
            handleThemeChange={handleFile("theme")}
            handleAvatarChange={handleFile("avatar")}
          />
          <Sidebar activeTab={activeTab} changeActiveTab={changeActiveTab} />
        </div>

        <ProfileForm
          teamList={(teams && teams.map((t: any) => t.name)) || []}
          getValues={getValues}
          activeTab={activeTab}
          hasUnsavedChanges={hasUnsavedChanges}
          register={register}
          watch={watch}
          setValue={setValue}
          errors={errors}
          regions={regions}
          openPassField={openPassField}
          togglePasswordVisibility={togglePasswordVisibility}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
        />
      </div>
    </Modal>
  );
}
